import { defineStore } from 'pinia';
import { ref } from 'vue';
import { api } from '@/lib/http';

export interface Lookups {
  states: { code: string; gstCode: string; name: string }[];
  divisions: { code: 'SOLAR' | 'TELECOM'; label: string }[];
  documentTypes: { code: string; label: string }[];
}

export interface Branding {
  legalName: string | null;
  tradeName: string | null;
  logoFileId: string | null;
}

/** App-wide UI state: theme, sidebar, branding and reference lookups. */
export const useAppStore = defineStore('app', () => {
  const theme = ref<'light' | 'dark'>(document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light');
  const sidebarCollapsed = ref(readBool('bsl-sidebar-collapsed'));
  const mobileNavOpen = ref(false);
  const lookups = ref<Lookups | null>(null);
  const branding = ref<Branding | null>(null);
  const unreadNotifications = ref(0);

  function setTheme(t: 'light' | 'dark') {
    theme.value = t;
    document.documentElement.dataset.theme = t;
    try {
      localStorage.setItem('bsl-theme', t);
    } catch {
      /* storage unavailable */
    }
  }

  function toggleSidebar() {
    sidebarCollapsed.value = !sidebarCollapsed.value;
    try {
      localStorage.setItem('bsl-sidebar-collapsed', String(sidebarCollapsed.value));
    } catch {
      /* storage unavailable */
    }
  }

  async function loadLookups() {
    lookups.value ??= await api.get<Lookups>('/lookups');
    return lookups.value;
  }

  async function loadBranding(force = false) {
    if (!branding.value || force) branding.value = await api.get<Branding>('/settings/branding');
    return branding.value;
  }

  async function refreshUnread() {
    try {
      unreadNotifications.value = (await api.get<{ unread: number }>('/notifications/unread-count')).unread;
    } catch {
      /* non-critical */
    }
  }

  return {
    theme,
    sidebarCollapsed,
    mobileNavOpen,
    lookups,
    branding,
    unreadNotifications,
    setTheme,
    toggleSidebar,
    loadLookups,
    loadBranding,
    refreshUnread,
  };
});

function readBool(key: string) {
  try {
    return localStorage.getItem(key) === 'true';
  } catch {
    return false;
  }
}
